/**
 * In-person / remote mode context (hybrid roles can switch).
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { useInterfaceElementsStore } from '@/stores/interfaceElementsStore';
import { useAuthStore } from '@/stores/authStore';
import { useMutation } from '@tanstack/react-query';
import { updateUserPreferences } from '@/services/preferences.service';
import { getInterfaceElements } from '@/services/interface-elements.service';
import { getInitialInterfacesToLoad } from '@/utils/dashboard-ie-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Staff app tutoring mode (hybrid roles can switch). */
export type AppMode = 'in_person' | 'remote';

/** Optional landing route after a mode switch (`/dashboard` if omitted). */
export type SetModeOptions = {
  navigateTo?: string;
};

/** In-person / remote switch state consumed by route dispatchers. */
interface ModeContextValue {
  /** Current app mode (in-person / remote). Null during initialization. */
  mode: AppMode | null;
  /** Switch to a different mode. No-op if the user's role doesn't support it. */
  setMode: (mode: AppMode, options?: SetModeOptions) => void;
  /** True if the authenticated user's role supports mode switching (hybrid role). */
  canSwitchMode: boolean;
  /** True while the interface store is being reloaded for a new mode. */
  isSwitching: boolean;
}

/** Default context before {@link ModeProvider} mounts (mode null, no-op setter). */
const ModeContext = React.createContext<ModeContextValue>({
  mode: null,
  setMode: () => undefined,
  canSwitchMode: false,
  isSwitching: false,
});

/** Props for {@link ModeProvider}. */
interface ModeProviderProps {
  children: React.ReactNode;
}

/** Persists `global.appMode` and reloads RBAC layouts when a hybrid user switches mode. */
export const ModeProvider: React.FC<ModeProviderProps> = ({ children }) => {
  const preferences = usePreferencesStore((state) => state.preferences);
  const updateInterfaceConfig = usePreferencesStore((state) => state.updateInterfaceConfig);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userMode = (user?.mode ?? null) as 'in_person' | 'remote' | 'hybrid' | null;
  const isHybridRole = userMode === 'hybrid';

  /**
   * Hybrid: last saved `global.appMode` (default in-person).
   * Single-mode: pin to `userMode`. Null until auth has a mode (avoids a flash).
   */
  const getInitialMode = (): AppMode | null => {
    if (userMode === null && !isHybridRole) return null;
    if (isHybridRole || (userMode as string) === 'hybrid') {
      const savedMode = preferences?.interfaceConfigs?.global?.appMode as AppMode | undefined;
      return savedMode ?? 'in_person';
    }
    if (userMode === 'remote') return 'remote';
    return 'in_person';
  };

  const [mode, setModeState] = React.useState<AppMode | null>(getInitialMode);
  const [isSwitching, setIsSwitching] = React.useState(false);

  /** Persist {@link UserPreferences.interfaceConfigs}.global.appMode on the user preferences document. */
  const { mutate: saveModePreference } = useMutation({
    mutationFn: (newMode: AppMode) =>
      updateUserPreferences({
        interfaceConfigs: {
          ...preferences?.interfaceConfigs,
          global: {
            ...(preferences?.interfaceConfigs?.global as any),
            appMode: newMode,
          },
        },
      }),
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  /**
   * Persist `global.appMode`, preload the new mode's RBAC layouts (same as login),
   * and send the user to `options.navigateTo` or `/dashboard`.
   */
  const setMode = React.useCallback(
    (nextMode: AppMode, options?: SetModeOptions) => {
      if (!isHybridRole || nextMode === mode) return;

      setModeState(nextMode);
      saveModePreference(nextMode);
      updateInterfaceConfig('global', {
        ...(preferences?.interfaceConfigs?.global as Record<string, unknown> | undefined),
        appMode: nextMode,
      });

      const store = useInterfaceElementsStore.getState();
      const interfaces = getInitialInterfacesToLoad(
        preferences?.interfaceConfigs?.['dashboard'],
        nextMode,
      );

      setIsSwitching(true);
      store.clearElements();
      interfaces.forEach((name) => store.startLoading(name));

      void navigate(options?.navigateTo ?? '/dashboard');

      Promise.all(interfaces.map((name) => getInterfaceElements(name, nextMode)))
        .then((results) => {
          const data: Record<string, any[]> = {};
          interfaces.forEach((name, i) => {
            data[name] = results[i];
          });
          useInterfaceElementsStore.getState().setInterfaceElements(data);
        })
        .catch((error) => {
          console.error('Mode switch reload failed:', error);
          interfaces.forEach((name) => useInterfaceElementsStore.getState().finishLoading(name));
        })
        .finally(() => {
          setIsSwitching(false);
        });
    },
    [isHybridRole, mode, saveModePreference, updateInterfaceConfig, preferences, navigate],
  );

  /** Pin single-mode users once `userMode` arrives; initialize hybrid from prefs if still null. */
  React.useEffect(() => {
    if (userMode === null) return;
    if (isHybridRole) {
      if (mode === null) {
        const savedMode = preferences?.interfaceConfigs?.global?.appMode as AppMode | undefined;
        setModeState(savedMode ?? 'in_person');
      }
      return;
    }
    if (userMode === 'remote') {
      setModeState('remote');
    } else {
      setModeState('in_person');
    }
  }, [isHybridRole, userMode, mode, preferences]);

  /** Follow `global.appMode` when another tab updates the saved preference. */
  React.useEffect(() => {
    if (isHybridRole) {
      const savedMode = preferences?.interfaceConfigs?.global?.appMode as AppMode | undefined;
      if (savedMode && savedMode !== mode) {
        setModeState(savedMode);
      }
    }
  }, [preferences, isHybridRole, mode]);

  const value = React.useMemo<ModeContextValue>(
    () => ({ mode, setMode, canSwitchMode: isHybridRole, isSwitching }),
    [mode, setMode, isHybridRole, isSwitching],
  );

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
};

/** Current app mode, switcher, and whether the role is hybrid. */
export const useMode = (): ModeContextValue => React.useContext(ModeContext);
